import React from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment';

const checkedWithinLast48Hours = lastChecked => (lastChecked == null ? null : moment().diff(lastChecked, 'hours', true) <= 48);
const checkedWithinLast12Months = lastChecked => (lastChecked == null ? null : moment().diff(lastChecked, 'months', true) <= 12);

function getValidationStatusText(tinCheckDetails) {
  if (!tinCheckDetails) {
    return null;
  }

  const checkedWithin48Hours = checkedWithinLast48Hours(tinCheckDetails.lastChecked);
  const checkedWithin12Months = checkedWithinLast12Months(tinCheckDetails.lastChecked);

  if (tinCheckDetails.tinMatched === null && (tinCheckDetails.lastChecked === null || !tinCheckDetails.lastChecked)) {
    return 'Validation check not made yet';
  }
  if (tinCheckDetails.responseCode === 100) {
    return 'IRS Service Unavailable. Retry in 24hrs';
  }
  if (tinCheckDetails.tinMatched === null && checkedWithin48Hours === false) {
    return `Attempted ${ moment(tinCheckDetails.lastChecked).format('MMM DD, YYYY') }`;
  }
  if (tinCheckDetails.tinMatched === null && checkedWithin48Hours) {
    return 'Attempted recently. Can try again in 48hrs';
  }
  if (tinCheckDetails.tinMatched !== null && tinCheckDetails.lastChecked && tinCheckDetails.lastChecked !== null) {
    if (checkedWithin12Months === true) {
      return `Checked ${ moment(tinCheckDetails.lastChecked).format('MMM DD, YYYY') }`;
    }
    return `Checked ${ moment(tinCheckDetails.lastChecked).format('MMM DD, YYYY') }. Suggest Re-check`;
  }

  return null;
}

function tinCheckSentRecently(tinCheckDetails) {
  if (!tinCheckDetails) {
    return null;
  }

  const checkedWithin48Hours = checkedWithinLast48Hours(tinCheckDetails.lastChecked);
  const checkedWithin12Months = checkedWithinLast12Months(tinCheckDetails.lastChecked);

  if (tinCheckDetails.tinMatched === null && checkedWithin48Hours === true) {
    return true;
  }
  if (tinCheckDetails.tinMatched === null && checkedWithin48Hours === false) {
    return false;
  }
  if (tinCheckDetails.tinMatched !== null && checkedWithin12Months === true) {
    return true;
  }

  return null;
}

function getTinCheckDetails(tinMatchResult) {
  const tinCheckDetails = {
    tinMatched: null,
    lastChecked: null,
    responseCode: null
  };

  if (!tinMatchResult || tinMatchResult === null) {
    return tinCheckDetails;
  }

  tinCheckDetails.lastChecked = tinMatchResult.requestTime;

  switch (tinMatchResult.responseCode) {
    case 0: // Match
    case 6: // UnknownTypeMatchOnlyOnSsn
    case 7: // UnknownTypeMatchOnlyOnEin
    case 8: // UnknownTypeMatchSsnAndEin
      tinCheckDetails.tinMatched = true;
      return tinCheckDetails;
    case 2: // TinNotIssued
    case 3: // NotMatched
      tinCheckDetails.tinMatched = false;
      return tinCheckDetails;
    case 100: // Error
      tinCheckDetails.responseCode = 100;
      return tinCheckDetails;
    default: // 1: TinMissing; 4: InvalidRequest; 5: DuplicateRequest
      return tinCheckDetails;
  }
}

function CheckNameOrTinChangedOnForm(tinMatchResultById, tinMatchFormProps) {
  if (!tinMatchResultById || !tinMatchFormProps) {
    return false;
  }

  const nameChanged = tinMatchResultById.name?.toLowerCase() !== tinMatchFormProps.name?.toLowerCase();

  const tinChanged = ((tinMatchResultById.tinType === 1 && tinMatchResultById.tin !== tinMatchFormProps.tin?.ssn)
      || (tinMatchResultById.tinType === 2 && tinMatchResultById.tin !== tinMatchFormProps.tin?.ein));

  return (nameChanged || tinChanged);
}

const TinValidationStatusChip = ({ tinMatched, lastChecked, nameOrTinChanged, responseCode }) => (
  <div>
    { !nameOrTinChanged && tinMatched === true && lastChecked !== null && <div className="chip chip--valid">Valid</div>}
    { !nameOrTinChanged && tinMatched === false && lastChecked !== null && <div className="chip chip--invalid">Invalid</div>}
    {(nameOrTinChanged || (tinMatched === null && lastChecked === null && responseCode !== 100)) && <div className="chip chip--unknown">Unknown</div>}
    { ((!nameOrTinChanged && tinMatched === null && (lastChecked !== null || responseCode === 100))) && <div className="chip chip--error">Error</div>}
  </div>
);
TinValidationStatusChip.propTypes = {
  tinMatched: PropTypes.bool,
  lastChecked: PropTypes.string,
  nameOrTinChanged: PropTypes.bool,
  responseCode: PropTypes.number
};
TinValidationStatusChip.defaultProps = {
  tinMatched: null,
  lastChecked: null,
  nameOrTinChanged: false,
  responseCode: 0
};

const TinCheckStatus = ({ onCheckTin, formId, tinMatchResult, workflowStage, hasValidation, isIndividualValidation, isImyChildResult,
  tinMatchFormProps, isFormDirty }) => {
  const tinMatchResultById = tinMatchResult?.find(result => result.formId === formId);

  const tinCheckDetails = getTinCheckDetails(tinMatchResultById);
  const irsRequestSucess = tinCheckDetails?.responseCode !== 100;
  const nameOrTinChanged = CheckNameOrTinChangedOnForm(tinMatchResultById, tinMatchFormProps);

  if ((tinMatchResultById?.checkMade === false && irsRequestSucess) || (nameOrTinChanged && tinCheckDetails)) {
    tinCheckDetails.tinMatched = null;
    tinCheckDetails.lastChecked = null;
  }

  const isTinMatchedNull = tinCheckDetails?.tinMatched === null;
  const isTinMatched = tinCheckDetails?.tinMatched === true;
  const matchedOver12MonthsAgo = isTinMatched && checkedWithinLast12Months(tinCheckDetails.lastChecked) === false;
  const formUnderReview = workflowStage > 4;
  const formComplete = workflowStage >= 100;

  const showCheckTinButton = ((formUnderReview || isIndividualValidation)
    && !formComplete) && hasValidation && !isImyChildResult
    && (isTinMatchedNull || matchedOver12MonthsAgo)
      && !isFormDirty;

  return (<div className="tin-validation-status">
    <label>TIN Validation:</label>
    <div className="status-bar col-12">
      <div className="status-label col-10">
        {tinCheckDetails && <TinValidationStatusChip
          tinMatched={ tinCheckDetails.tinMatched }
          lastChecked={ tinCheckDetails.lastChecked }
          responseCode={ tinCheckDetails.responseCode }
          nameOrTinChanged={ nameOrTinChanged }
        />}
        {tinCheckDetails && <div className="last-checked-label">{getValidationStatusText(tinCheckDetails)}</div>}
      </div>
      <div className="button-container col-2">
        {showCheckTinButton && !nameOrTinChanged && tinCheckDetails && tinCheckDetails.responseCode !== 100 && tinCheckSentRecently(tinCheckDetails) && <div className="check-tin-sent">Sent</div>}
        {showCheckTinButton && (nameOrTinChanged || (!tinCheckSentRecently(tinCheckDetails) || (tinCheckDetails && tinCheckDetails.responseCode === 100)))
          && <button
            type="button"
            className="btn text-ellipsis check-tin-button"
            onClick={ () => onCheckTin(formId, matchedOver12MonthsAgo || !irsRequestSucess) }
          >Check</button>}
      </div>
    </div>
  </div>);
};
TinCheckStatus.propTypes = {
  onCheckTin: PropTypes.func.isRequired,
  formId: PropTypes.number.isRequired,
  tinMatchResult: PropTypes.arrayOf(PropTypes.shape()),
  workflowStage: PropTypes.number,
  hasValidation: PropTypes.bool,
  isIndividualValidation: PropTypes.bool,
  isImyChildResult: PropTypes.bool,
  tinMatchFormProps: PropTypes.shape(),
  isFormDirty: PropTypes.bool
};
TinCheckStatus.defaultProps = {
  tinMatchResult: null,
  hasValidation: true,
  isIndividualValidation: false,
  isImyChildResult: false,
  workflowStage: 0,
  tinMatchFormProps: null,
  isFormDirty: false
};

export default TinCheckStatus;
